import { useUserOrgId } from '../../../auth/authentication';
import { AxleInfoField } from '../../../components/displayFields/AxleInfoField';
import { QualificationsField } from '../../../components/formFields/QualificationsField';
import {
    AxleDialog,
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogPrimaryButton,
} from '../../../components/layout/AxleDialog';
import { If } from '../../../components/utils/conditionalComponents';
import {
    DemandConnection,
    useEditDemandConnection,
    useEditDemandConnectionOptions,
} from '../../../fetching/api/supplyAndDemandConnections';
import { useInputState, useJointInputState } from '../../../utils/inputState';
import { ReturnComponent } from '../../../utils/reactTypes';

const EditDemandConnectionDialogContents = ({
    close,
    demandConnection,
}: {
    close: () => void;
    demandConnection: DemandConnection;
}): ReturnComponent => {
    const userOrgId = useUserOrgId();
    const { qualificationOptions } = useEditDemandConnectionOptions(
        demandConnection.handshake.uuid,
    );

    const editDemandConnection = useEditDemandConnection(demandConnection.handshake.uuid);

    const qualificationIdsState = useInputState<string[]>(
        demandConnection.qualification_requirements.map((q) => q.uuid),
        {
            defaultHelperText: `Any visits that come to ${demandConnection.handshake.to_org.name} from ${demandConnection.handshake.from_org.name} will require these qualifications.`,
        },
    );

    const { checkDifferent, checkValid } = useJointInputState([qualificationIdsState]);

    return (
        <>
            <AxleDialogContent>
                <If condition={demandConnection.handshake.to_org.uuid !== userOrgId}>
                    <AxleInfoField
                        label={'Organization'}
                        value={`${demandConnection.handshake.to_org.name}`}
                    />
                </If>
                <AxleInfoField
                    label={'Demand Connection'}
                    value={`${demandConnection.handshake.from_org.name}`}
                />
                <QualificationsField
                    label={'Qualifications Required'}
                    inputState={qualificationIdsState}
                    options={qualificationOptions}
                />
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogPrimaryButton
                    label={'Save'}
                    onClick={() => {
                        if (!checkDifferent()) return;
                        if (!checkValid()) return;
                        void editDemandConnection(
                            {
                                qualificationIds: qualificationIdsState.value,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

export const EditDemandConnectionDialog = ({
    isOpen,
    close,
    demandConnection,
}: {
    isOpen: boolean;
    close: () => void;
    demandConnection: DemandConnection | null;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='edit-demand-connection-dialog'
            isOpen={isOpen}
            close={close}
            title={'Edit Demand Connection'}
        >
            {demandConnection && (
                <EditDemandConnectionDialogContents
                    close={close}
                    demandConnection={demandConnection}
                />
            )}
        </AxleDialog>
    );
};
